/**
 * CAP-40007 TCL-2727 - JWT Auth Interceptor (PingFederate).
 * Attaches the PingFederate access token as a Bearer header to outgoing API requests
 * (skips requests to the IdP itself). On HTTP 401 it restarts the PingFederate login flow.
 */
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { OidcAuthService } from './oidc-auth.service';
import { AppConfig } from '../app.config';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  // Track retry attempts per URL to implement exponential backoff
  private retryAttempts = new Map<string, number>();
  private readonly MAX_RETRIES = 2;

  constructor(private oidc: OidcAuthService, private messageService: MessageService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // CAP-40007 TCL-2727 - Never attach the API token to PingFederate (token/discovery) calls
    const issuer = AppConfig.settings ? AppConfig.settings.issuerURL : '';
    if (issuer && req.url.indexOf(issuer) === 0) {
      
      return next.handle(req).pipe(catchError(err => this.handleError(err)));
    }

    // CAP-40007 TCL-2727 - Attach the PingFederate access token to every API request
    const token = this.oidc.getAccessToken();
    if (token) {
   
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    } else {
      
    }

    return next.handle(req).pipe(catchError(err => this.handleError(err)));
  }

  /**
   * CAP-40007 TCL-2727 - Handle HTTP 401 Unauthorized: notify and restart PingFederate login.
   * BUGFIX (400 Too Many Calls) - Added exponential backoff to prevent infinite retry loops
   * on token endpoint 400 errors.
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    const url = error.url || 'unknown';
    
    if (error.status === 401) {
  
      this.messageService.add({
        severity: 'error',
        summary: 'Session expirée',
        detail: 'Votre session a expiré. Veuillez vous reconnecter.',
        life: 5000
      });
      this.oidc.login();
    } else if (error.status === 400) {
      // BUGFIX (400 Too Many Calls) - Track 400 retries and stop after MAX_RETRIES
      const retries = this.retryAttempts.get(url) || 0;
   
      
      if (retries >= this.MAX_RETRIES) {
      
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur d\'authentification',
          detail: 'Impossible de se connecter. Vérifiez la configuration SSO.',
          life: 5000
        });
        this.retryAttempts.delete(url);
        return throwError(error);
      }
      
      this.retryAttempts.set(url, retries + 1);
    } else if (error.status >= 500) {

    } else {
  
    }
    
    return throwError(error);
  }
}
